/**
 * TEMP-H-016: Theme Provider Component
 * 
 * Provides theme context and manages theme switching with localStorage persistence.
 */

import { createContext, useLayoutEffect, useMemo, useState, useCallback } from 'react';
import type { ThemeContext, ThemeProviderProps, ThemeId } from './types';
import {
  AVAILABLE_THEMES,
  getSavedTheme,
  saveTheme,
  applyTheme,
  DEFAULT_THEME
} from './theme-registry';
import { persistTheme } from '@/lib/theme-persistence';
import { useServerAuth } from '@/hooks/useAuth-cookie';

// Note: Theme CSS files should be imported here when available
// import '../styles/themes/agi.theme.css';
// import '../styles/themes/dark.theme.css';

// Create context with default values that match `useTheme` expectations
const defaultContext: ThemeContext = {
  themeId: DEFAULT_THEME,
  setThemeId: async () => {},
  setTheme: async () => {},
  availableThemes: AVAILABLE_THEMES,
  isLoading: false,
};

const ThemeContextImpl = createContext<ThemeContext>(defaultContext);
export { ThemeContextImpl as ThemeContext };

/**
 * Theme provider that handles theme management and persistence
 */
export function ThemeProvider({ children, initialTheme }: ThemeProviderProps) {
  const [themeId, setThemeIdState] = useState<ThemeId>(initialTheme || getSavedTheme());
  const [isLoading, setIsLoading] = useState(false);

  // DV-FIX-310: Get auth state to defer backend persistence until auth is ready
  const { user, loading: authLoading } = useServerAuth();
  const isAuthenticated = !!user && !authLoading;

  // Apply theme immediately when changed, but defer backend persistence
  useLayoutEffect(() => {
    applyTheme(themeId);

    // DV-FIX-310: Only persist to backend when authenticated to prevent 400 errors during auth bootstrap
    if (isAuthenticated) {
      // Persist theme with graceful error handling
      persistTheme(themeId).catch((error) => {
        // Only log in development, don't throw errors in production
        if (import.meta.env.DEV) {
          console.log('Theme persistence failed (fallback to localStorage only):', error.message);
        }
      });
    } else if (import.meta.env.DEV) {
      console.log('🎨 Theme applied locally, backend persistence deferred until auth ready:', themeId);
    }
  }, [themeId, isAuthenticated]);

  // Centralized theme setter with loading states and error handling
  const setThemeId = useCallback(async (newThemeId: ThemeId) => {
    if (newThemeId === themeId) return;

    setIsLoading(true);
    try {
      // Apply theme immediately for instant feedback
      applyTheme(newThemeId);
      setThemeIdState(newThemeId);

      // Always persist to localStorage
      saveTheme(newThemeId);

      // DV-FIX-310: Only persist to backend when authenticated to prevent 400 errors
      if (isAuthenticated) {
        await persistTheme(newThemeId);
      } else if (import.meta.env.DEV) {
        console.log('🎨 Theme change applied locally, backend persistence deferred until auth ready:', newThemeId);
      }
    } catch (error) {
      console.error('Failed to change theme:', error);
      // Revert on error
      applyTheme(themeId);
    } finally {
      setIsLoading(false);
    }
  }, [themeId, isAuthenticated]);

  const contextValue = useMemo<ThemeContext>(() => ({
    themeId,
    setThemeId,
    setTheme: setThemeId, // Alias for backward compatibility
    availableThemes: AVAILABLE_THEMES,
    isLoading,
  }), [themeId, setThemeId, isLoading]);

  return (
    <ThemeContextImpl.Provider value={contextValue}>
      {children}
    </ThemeContextImpl.Provider>
  );
}

// Export error boundary placeholder for compatibility
export function ThemeProviderErrorBoundary({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}